import { AfterViewInit, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, Renderer2 } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {  NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { editfaq, FAQ } from 'src/app/core/constants/core.constant';
import { CognitoStyleService } from 'src/app/core/services/cognito-style/cognito-style.service';
import * as _ from 'lodash';
import { COGNITO_CSS_REGEX } from 'src/app/shared/constants/common';

@Component({
  selector: 'app-email-cognito-form',
  templateUrl: './email-cognito-form.component.html',
  styleUrls: ['./email-cognito-form.component.scss']
})
export class EmailCognitoFormComponent implements OnChanges, AfterViewInit,OnDestroy {
  @Input() formId: number;
  @Input() cogFormPrefillData: any;
  cogFormId: number;
  hasUnsavedChanges: boolean = false;
  modalReference: NgbModalRef;
  constants = FAQ;
  constantMsg = editfaq;
  savedResponse: any;
  isModelOpen: boolean = false;
  iframeSrc: any = '';
  submitted: boolean;
  parkingFormRegex: any = COGNITO_CSS_REGEX;

  constructor(private readonly renderer: Renderer2, private readonly sanitizer: DomSanitizer, private readonly cdRef: ChangeDetectorRef, private readonly cognitoStyleService: CognitoStyleService) { }

  ngOnChanges(): void {
    this.cogFormId = this.formId;
  }

  ngAfterViewInit(): void {
    this.cleanupCognito();
    // setTimeout(() => {
    this.injectCognitoForm();
    // }, 100);
    this.cdRef.detectChanges();
  }

  /**
 * injectCognitoForm() - This method dynamically injects the Cognito form iframe and script into the page.
 * It first sanitizes the Cognito form URL to avoid security issues, then creates and appends the script 
 * tag that loads the Cognito form iframe. After loading the script, it binds the necessary event listeners
 * and pre-fills the form.
 * 
 * The method also uses `setTimeout` to ensure the script is appended after the current browser operations
 * are completed.
 */

private injectCognitoForm(): void {
  const scriptId = this.cogFormId;
  const rawUrl = `https://www.cognitoforms.com/f/pj-q73P-xESkhPtMGKGk9A/${scriptId}`;
  const allowedPattern = /^https:\/\/www\.cognitoforms\.com\/f\/[A-Za-z0-9\-]+\/[A-Za-z0-9\-]+$/;

  if (!allowedPattern.test(rawUrl)) {
    console.error('Blocked unsafe URL:', rawUrl);
    return;
  }
  this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(rawUrl);
  const script = this.renderer.createElement('script');
  script.src = 'https://www.cognitoforms.com/f/iframe.js';
  script.type = 'text/javascript';
  script.async = true;
  script.onload = () => {
    this.waitForCognitoAndBindEvents();
  };
 
  this.renderer.appendChild(document.body, script);
}

  /**
 * waitForCognitoAndBindEvents() - This method repeatedly checks if the Cognito form iframe and the `Cognito`
 * object are available on the page. Once both are ready, it clears the interval and proceeds to bind 
 * the form submission events (`beforeSubmit` and `afterSubmit`) and pre-fills the form with necessary data.
 * 
 * This method ensures that the form and its related scripts are fully loaded before interacting with them.
 */
  private waitForCognitoAndBindEvents(): void {
    // const interval = setInterval(() => {
    const Cognito = (window as any).Cognito;
    const formReady = document.querySelector('iframe[src*="cognitoforms.com"]');

    if (Cognito && formReady) {
      this.addFormSubmitListener();
      this.prefillForm();
    }
    // }, 200);
  }

  /**
 * addFormSubmitListener() - This method listens for form submission events (`beforeSubmit` and `afterSubmit`)
 * from the Cognito form. It attaches event listeners to handle form state changes (such as showing/hiding the model)
 * and tracking unsaved changes.
 * 
 * - The `beforeSubmit` event is triggered before the form is submitted, which sets the model as open and marks
 *   unsaved changes.
 * - The `afterSubmit` event is triggered after the form is submitted, which closes the model and marks the changes as saved.
 */
  private addFormSubmitListener(): void {
    const Cognito = (window as any).Cognito;
    if (Cognito && Cognito.on) {
      Cognito.off?.('beforeSubmit');
      Cognito.off?.('afterSubmit');

      Cognito.on('beforeSubmit', () => {
        this.isModelOpen = true;
        this.hasUnsavedChanges = true;
      });

      Cognito.on('afterSubmit', () => {
        this.isModelOpen = false;
        this.hasUnsavedChanges = false;
      });
    }
  }

  /**
 * injectCognitoCss(css: string) - This method dynamically injects custom CSS styles into the document head.
 * It is used as a fallback mechanism to apply styling to Cognito forms when the built-in `setCss()` method 
 * does not reliably apply styles across all environments or form instances.
 * 
 * The method creates a `<style>` element, sets its content to the provided CSS string, and appends it to 
 * the `<head>` of the document. This ensures that the styles are globally available and applied to the 
 * Cognito form rendered in the DOM.
 * 
 * @param css - A string containing valid CSS rules to be injected into the document.
 */

  private injectCognitoCss(css: string): void {
  const style = document.createElement('style');
  style.innerHTML = css;
  document.head.appendChild(style);
}

  /**
 * prefillForm() - This method pre-fills a Cognito form with data retrieved from the Angular component.
 * It injects values such as project name, company name, project ID, user details, etc., into the form 
 * fields before the form is displayed. It also customizes the appearance of the form using CSS.
 * 
 * It uses the `Cognito` object exposed on the `window` to interact with the form (using jQuery-like syntax)
 * and pre-populate the input fields with relevant information.
 */
  private prefillForm(): void {
    if (window.hasOwnProperty('Cognito')) {

      (window as any).Cognito("#form1").prefill({
        "Requestor": this.cogFormPrefillData?.requestedByEmail,
        "UserId": this.cogFormPrefillData?.requestedById,
        "ApplicantEmail": this.cogFormPrefillData?.requestedFor,
        "CompanyId": this.cogFormPrefillData?.externalServiceReqDetail?.companyDetails?.companyId,
        "ProductionCompany": this.cogFormPrefillData?.externalServiceReqDetail?.companyDetails?.companyName,
        "ProjectId": this.cogFormPrefillData?.externalServiceReqDetail?.projectDetails?.projectId,
        "ProjectName": this.cogFormPrefillData?.externalServiceReqDetail?.projectDetails?.projectName,
        "ExtInfoId": this.cogFormPrefillData?.extInfoId
      });
      
    const css = this.cognitoStyleService.getCognitoCss();
    const updatedCss = _.replace(css, this.parkingFormRegex?.textTransformRegex, this.parkingFormRegex?.textTransformRegexReplacement);
    this.injectCognitoCss(updatedCss); // fallback if setCss doesn't work
    (window as any).Cognito("#form1").setCss(updatedCss);
    }
  }
  /**
 * cleanupCognito() - This method is responsible for cleaning up any Cognito-related resources 
 * when navigating away from the form or when the component is destroyed. It ensures that there 
 * are no lingering scripts or event listeners that could cause memory leaks or unwanted behavior.
 * 
 * It removes the Cognito iframe and script from the DOM and also removes any previously attached
 * event listeners for form submission (e.g., `beforeSubmit` and `afterSubmit`).
 * 
 * The method also ensures that the global `Cognito` object is deleted to avoid conflicts
 * when the form page is reloaded.
 */
  private cleanupCognito(): void {
    const oldIframe = document.querySelector('iframe[src*="cognitoforms.com"]');
    if (oldIframe) oldIframe.remove();

    const oldScript = document.querySelector('script[src="https://www.cognitoforms.com/f/iframe.js"]');
    if (oldScript) oldScript.remove();

    const Cognito = (window as any).Cognito;
    if (Cognito?.off) {
      Cognito.off('beforeSubmit');
      Cognito.off('afterSubmit');
    }

    delete (window as any).Cognito;
  }

  /**
 * hasUnsavedChangesValue() - This method is used to update the `hasUnsavedChanges` state.
 * It helps track whether there are unsaved changes in the form.
 * 
 * @param value - A boolean value indicating whether there are unsaved changes. 
 *                `true` means there are unsaved changes, `false` means there are no unsaved changes.
 */
  hasUnsavedChangesValue(value: boolean): void {
    this.hasUnsavedChanges = value;
  }

  /**
 * ngOnDestroy() - This Angular lifecycle method is called when the component is about to be destroyed.
 * It is used to clean up any resources that are no longer needed, such as unsubscribing from observables
 * or removing event listeners to avoid memory leaks.
 * 
 * In this case, it calls the `cleanupCognito()` method to ensure that any resources related to Cognito
 * forms and listeners are properly cleaned up when the component is destroyed.
 */
  ngOnDestroy(): void {
    this.cleanupCognito();
  }
}
